import { initializeAdalongWidget } from '@adalong/widget';
import type { AdalongWidgetInstance } from '@adalong/widget';
import { ADALONG_COLORS } from '@src/styles';
import { useEffect, useState } from 'react';
import { stylesheet } from 'typestyle';

interface Props {
  token: string;
}

type WidgetStatus = 'loading' | 'ready' | 'empty' | 'error';
const WIDGET_HOST_ID = 'adalong-latest-ugc-agreed-widget';

export function LatestUGCAgreedWidget({ token }: Props) {
  const [status, setStatus] = useState<WidgetStatus>('loading');

  useEffect(() => {
    let cancelled = false;
    let instance: AdalongWidgetInstance | undefined;
    setStatus('loading');

    const loadWidget = async () => {
      try {
        instance = await initializeAdalongWidget({
          token,
          config: {
            hasAnalyticsConsent: false,
          },
        });
        if (cancelled) {
          instance.destroy();
          return;
        }

        instance.onEvent('minContentNotReached', () => {
          if (!cancelled) setStatus('empty');
        });
        instance.onEvent('widgetLoaded', ({ posts }) => {
          if (!cancelled) setStatus(posts.length > 0 ? 'ready' : 'empty');
        });
        instance.onEvent('widgetLoadingFailed', () => {
          if (!cancelled) setStatus('error');
        });
        await instance.load(`#${WIDGET_HOST_ID}`);
      } catch (_error) {
        if (!cancelled) setStatus('error');
      }
    };

    void loadWidget();
    return () => {
      cancelled = true;
      instance?.destroy();
    };
  }, [token]);

  const isVisible = status === 'ready';

  return (
    <section
      className={sheet.panel}
      aria-label="Latest UGC Agreed"
      aria-hidden={!isVisible}
      style={isVisible ? undefined : hiddenPanelStyle}
    >
      <h2 className={sheet.title}>Latest UGC Agreed</h2>
      <div id={WIDGET_HOST_ID} className={sheet.widget} />
    </section>
  );
}

const hiddenPanelStyle = {
  position: 'absolute' as const,
  left: -10000,
  visibility: 'hidden' as const,
  pointerEvents: 'none' as const,
};

const sheet = stylesheet({
  panel: {
    boxSizing: 'border-box',
    width: 'calc(100% - 48px)',
    maxWidth: 1048,
    margin: '0 auto',
    padding: 24,
    border: '1px solid rgba(255, 255, 255, 0.21)',
    borderRadius: 12,
    backgroundColor: ADALONG_COLORS.DARK_BLACK,
  },
  title: {
    margin: '0 0 20px',
    color: ADALONG_COLORS.LIGHT_WHITE,
    fontSize: 18,
    fontWeight: 600,
  },
  widget: {
    width: '100%',
    minHeight: 240,
  },
});
